<template>
  <div :class="tableContainerClass">
    <div class="relative">
      <table class="min-w-full divide-y divide-gray-200">
        <TableHeader
          :activeState="activeState"
          :isPlanning="isPlanning"
          :isFullScreen="isFullScreen"
        />
        <tbody
          class="divide-y-2 divide-gray-500"
          style="background-color: #3a3a3a"
        >
          <template v-if="getLoaderLength() != 0 && orders.length">
            <TableRow
              v-for="item in visibleOrders"
              :key="item.orderNo || item.id"
              :item="item"
              :activeState="activeState"
              :isPlanning="isPlanning"
              :products="products"
              :vehicleList="memoizedVehicles"
              :customers="customers"
              @select-item="$emit('select-order', item)"
              @view-order="$emit('view-order', item)"
              @remove-order="$emit('remove-order', item)"
              @change-vehicle="(val, isManualTyped) => handleVehicleChange(val, isManualTyped, item)"
              @change-driver="handleDriverChange($event, item)"
              @handle-personel-change="handlePersonnelChange($event, item)"
              @open-context-menu="handleContextMenu($event, item)"
              @isVehicleConflictModalVisible="handleConflictModal"
              @distance-calculated="$emit('distance-calculated', $event)"
            />
          </template>
          <TableLoader
            :load="loading"
            :length="getLoaderLength()"
            :colspan="activeState === 2 ? 13 : 11"
          />
          <tr v-if="planningLoad">
            <td colspan="8" class="text-center py-4 text-white">
              <i class="fas fa-spinner fa-spin"></i> Planlanıyor
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <VehicleConflictModal 
      @close="reset" 
      :visible="isVehicleConflictModalVisible.show" 
      :orderInfo="isVehicleConflictModalVisible.order" 
      :vehicleInfo="isVehicleConflictModalVisible.vehicle" 
    />
  </div>
</template>
  
<script>
import TableHeader from "./TableHeader.vue";
import TableRow from "./TableRow.vue";
import TableLoader from "@/components/general/table-loader.vue";
import VehicleConflictModal from './VehicleConflictModal.vue';

export default {
  name: "OrdersTable",
  components: {
    TableHeader,
    TableRow,
    TableLoader,
    VehicleConflictModal
  },
  props: {
    isFullScreen: {
      type: Boolean,
      default: false,
    },
    activeState: {
      type: Number,
      required: true,
    },
    orders: {
      type: Array,
      required: true,
    },
    orderCount: {
      type: Object,
    },
    planningLoad: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      required: true,
    },
    vehicleList: {
      type: Array,
      required: true,
    },
    customers: {
      type: Array,
      required: true,
    },
    isPlanning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVehicleConflictModalVisible: {
        show: false,
        order: {},
        vehicle: {}
      },
      // Virtualization için değişkenler
      itemsPerPage: 30,
      currentPage: 1,
      observer: null,
      memoizedVehiclesCache: null,
      vehicleListVersion: 0
    }
  },
  created() {
    // İlk oluşturulduğunda araç listesini önbelleğe al
    this.updateVehicleCache();
  },
  mounted() {
    // Sonsuz kaydırma için Intersection Observer
    this.setupInfiniteScroll();
  },
  beforeDestroy() {
    // Component yok edilmeden önce observer'ı temizle
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    getLoaderLength() {
      if( this.activeState === 1) {
        return (this.orderCount.unplanned === 0 || this.orderCount.unplanned === -1 ) ? 0 : this.orders.length;
      }
      if( this.activeState === 3) {
        return this.orderCount.error === 0 ? 0 : this.orders.length;
      }
    },
    reset() {
      this.isVehicleConflictModalVisible = {
        show: false,
        order: {},
        vehicle: {}
      };
    },
    handleConflictModal(event) {
      this.isVehicleConflictModalVisible = event;
    },
    handleVehicleChange(val, isManualTyped, item) {
      this.$emit('change-vehicle', val, item, isManualTyped);
    },
    handleDriverChange(driver, item) {
      this.$emit('change-driver', driver, item);
    },
    handlePersonnelChange(driver, item) {
      this.$emit('handle-personel-change', driver, item);
    },
    handleContextMenu(event, item) {
      this.$emit('open-context', event, item);
    },
    updateVehicleCache() {
      // Araç listesini önbelleğe al
      this.memoizedVehiclesCache = this.vehicleList.map((item) => ({
        ...item,
        name: item.vehicle,
        isRent: item?.haveType,
      }));
      this.vehicleListVersion++;
    },
    setupInfiniteScroll() {
      // Sonsuz kaydırma için IntersectionObserver oluştur
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && 
              this.currentPage * this.itemsPerPage < this.orders.length) {
            this.currentPage++;
          }
        });
      }, options);
      
      // Sentinel eleman oluştur ve takip et
      const sentinel = document.createElement('div');
      sentinel.id = 'scroll-sentinel';
      sentinel.style.height = '0px';
      sentinel.style.marginTop = '-1px';

      
      const tableElement = this.$el.querySelector('tbody');
      if (tableElement) {
        tableElement.appendChild(sentinel);
        this.observer.observe(sentinel);
      }
    }
  },
  computed: {
    tableContainerClass() {
      return !this.isFullScreen
        ? 'shadow table-height-value overflow-y-scroll border-b border-gray-200 bg-gray-200 dark:bg-gray-900 sm:rounded-lg mt-4'
        : 'w-full overflow-y-scroll h-screen pb-28 mt-4';
    },
    visibleOrders() {
      return this.orders.slice(0, this.currentPage * this.itemsPerPage);
    },
    memoizedVehicles() {
      // Önbelleğe alınmış araç verilerini kullan
      return this.memoizedVehiclesCache;
    }
  },
  watch: {
    vehicleList: {
      handler() {
        // Araç listesi değiştiğinde önbelleği güncelle
        this.updateVehicleCache();
      },
      deep: true
    },
    orders() {
      // Sipariş listesi değiştiğinde sayfayı sıfırla
      this.currentPage = 1;
      
      // Observer sentinel'ı güncelle - varsa
      this.$nextTick(() => {
        const sentinel = document.getElementById('scroll-sentinel');
        if (sentinel && this.observer) {
          this.observer.unobserve(sentinel);
          this.observer.observe(sentinel);
        } else {
          this.setupInfiniteScroll();
        }
      });
    }
  }
};
</script>